/**
 * useSession — the facade the whole flow reads from.
 *
 * Combines the route token (`/r/:token` — the link IS the session) with the server-resolved
 * context (TanStack Query) and derives the resumable step + guard helpers. Screens never fetch
 * context themselves; they call `useSession()`.
 */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { RefereeContext } from '@/lib/api-types';
import { useSessionStore } from './store';
import { canAccess, isTerminalStatus, redirectStepFor, statusToStep, type RefereeStep } from './steps';

export const CONTEXT_STALE_MS = 15_000;

export function refereeContextQueryKey(token: string) {
  return ['referee-context', token] as const;
}

export interface Session {
  token: string;
  context: RefereeContext | undefined;
  /** The step the referee belongs on right now (for step restoration). */
  currentStep: RefereeStep | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  /** Guard: may the referee view `target` in the current state? */
  canAccess: (target: RefereeStep) => boolean;
}

export function useSession(): Session {
  const { token = '' } = useParams<{ token: string }>();

  // Bind the client-only store to this link. Everything in it — the consent ack, the mic check,
  // the held take, the verification code the sign response returned — is a fact about ONE
  // reference request, so opening a different link in the same tab must start from nothing.
  // Otherwise link B's Done screen can show link A's verification code, and a referee is shown a
  // stranger's. See `SessionState.tokenScope` for the per-field reasoning.
  //
  // A change of token, NOT a mount: `bindToken` no-ops when the token is unchanged, which is what
  // makes it safe to call from the hook every screen uses. Guarded on a truthy token so a route
  // rendered outside the `/r/:token` subtree can never bind '' and wipe a live session.
  useEffect(() => {
    if (token) useSessionStore.getState().bindToken(token);
  }, [token]);

  const query = useQuery({
    queryKey: refereeContextQueryKey(token),
    queryFn: () => apiClient.getContext(token),
    enabled: token.length > 0,
    staleTime: CONTEXT_STALE_MS,
    retry: 1,
  });

  const context = query.data;

  // The other end of the take's life. A request that has left the flow — withdrawn, expired,
  // declined, held for review, or already signed — can never accept a recording again, so a take
  // still held for it is three minutes of someone's voice with no future. Drop it here rather than
  // let it sit in memory until the tab closes; the guards are meanwhile relocating them anyway.
  const terminal = context ? isTerminalStatus(context.status) : false;
  useEffect(() => {
    if (terminal) useSessionStore.getState().clearRecording();
  }, [terminal]);

  return useMemo<Session>(() => {
    const currentStep = context ? statusToStep(context.status) : undefined;
    return {
      token,
      context,
      currentStep,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: () => void query.refetch(),
      canAccess: (target) => (context ? canAccess(context.status, target) : false),
    };
    // query.refetch is stable; depend on the observable state that changes the derived values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, context, query.isLoading, query.isError, query.error]);
}

export { redirectStepFor };
